import { Stack } from "@mui/material";
import { forwardRef } from "react";

import { useImageLoader } from "@/hooks/useImageLoader";
import type { AspectRatio, ImageTransform } from "@/types";

import { CircularProgress } from "..";
import { DottedBackground } from "../Backgrounds/Dotted";
import { ImageStage, ImageStageRef } from "./components/ImageStage/ImageStage";
import { Size } from "./types";

export interface ImageEditorProps {
  image: string;
  stageSize?: Size;

  aspectRatio?: AspectRatio;
  aspectRatios?: AspectRatio[];
  onAspectRatioChange?: (aspectRatio: AspectRatio) => void;

  transform?: ImageTransform;
  onTransformChange?: (transform: ImageTransform) => void;
}

const DEFAULT_ASPECT_RATIOS = [
  {
    type: "SQUARE",
    width: 1,
    height: 1,
  },
  {
    type: "LANDSCAPE_16_9",
    width: 16,
    height: 9,
  },
  {
    type: "LANDSCAPE_4_3",
    width: 4,
    height: 3,
  },
  {
    type: "PORTRAIT_9_16",
    width: 9,
    height: 16,
  },
  {
    type: "PORTRAIT_3_4",
    width: 3,
    height: 4,
  },
] satisfies AspectRatio[];

export const ImageEditor = forwardRef<ImageStageRef, ImageEditorProps>(
  (props: ImageEditorProps, ref) => {
    const {
      image,
      stageSize = { width: 900, height: 480 },
      aspectRatio = DEFAULT_ASPECT_RATIOS[0],
      aspectRatios = DEFAULT_ASPECT_RATIOS,
      onAspectRatioChange,
      transform,
      onTransformChange,
    } = props;

    const { image: loadedImage } = useImageLoader(image);

    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <DottedBackground alignItems="center" justifyContent="center">
          {loadedImage && (
            <ImageStage
              ref={ref}
              stageSize={stageSize}
              image={loadedImage}
              aspectRatio={aspectRatio}
              aspectRatios={aspectRatios}
              onAspectRatioChange={onAspectRatioChange}
              transform={transform}
              onTransformChange={onTransformChange}
            />
          )}
          {!loadedImage && (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                height: stageSize.height,
                width: "100%",
              }}
            >
              <CircularProgress />
            </Stack>
          )}
        </DottedBackground>
      </Stack>
    );
  },
);
